/**
 * Presets as data (brief DET-04, 5.2 step 1).
 *
 * A preset is hints and conventions, never hidden behavior: name
 * boosts consumed by role detection (each boost leaves a reason in
 * the score), a respondent term for the message catalog, the
 * demographic name list, and the default dichotomous recode the
 * rule builder proposes. Every hint is a signal; the user confirms
 * assignments exactly as with the generic preset.
 */
export const GENERIC_PRESET = {
    id: 'generic',
    name: 'Generic survey',
    respondentTerm: 'respondents',
    hints: [],
    demographicNames: [
        'sex',
        'gender',
        'age',
        'agegrp',
        'agegroup',
        'grade',
        'race',
        'raceeth',
        'ethnicity',
        'hispanic',
        'identity',
        'orientation',
    ],
    dichotomousRule: null,
};
/**
 * School survey preset (brief DET-04; renamed from the original
 * survey-specific preset to a universal name, owner direction
 * 2026-10-18, Decision Log Addendum 22). School-survey files of
 * this family name the design variables `weight`, `stratum`, and
 * `psu`; dichotomous question variables are QN followed by
 * digits, computed variables QN followed by letters; the
 * standard dichotomous coding is 1 = yes, 2 = no. Names vary by
 * year and version, so these are boosts, not rules.
 */
export const SCHOOL_SURVEY_PRESET = {
    id: 'school-survey',
    name: 'School Survey',
    respondentTerm: 'students',
    hints: [
        {
            role: 'weight',
            names: ['weight'],
            boost: 0.2,
            reason: 'School survey preset: the analysis weight is named "weight" in these files',
        },
        {
            role: 'stratum',
            names: ['stratum'],
            boost: 0.2,
            reason: 'School survey preset: the stratum variable is named "stratum" in these files',
        },
        {
            role: 'psu',
            names: ['psu'],
            boost: 0.2,
            reason: 'School survey preset: the PSU variable is named "psu" in these files',
        },
        {
            role: 'indicator',
            pattern: /^qn\d+$/i,
            boost: 0.25,
            reason: 'School survey preset: QN followed by digits marks a dichotomous question variable',
        },
        {
            role: 'indicator',
            pattern: /^qn[a-z]/i,
            boost: 0.15,
            reason: 'School survey preset: QN followed by letters marks a computed dichotomous variable',
        },
    ],
    demographicNames: [
        'age',
        'sex',
        'grade',
        'raceeth',
        'race4',
        'race',
        'ethnicity',
        'hispanic',
        'sexid',
        'sexualid',
    ],
    dichotomousRule: { yesValues: [1], noValues: [2] },
};
export const PRESETS = [
    GENERIC_PRESET,
    SCHOOL_SURVEY_PRESET,
];
export function presetById(id) {
    // Legacy alias: projects saved before the 2026-10-18 rename
    // carry the old preset id; resolve it to the renamed preset.
    if (id === 'yrbs')
        return SCHOOL_SURVEY_PRESET;
    return PRESETS.find((p) => p.id === id) ?? GENERIC_PRESET;
}
/**
 * Preset selection from file signatures (brief 5.2 step 1): the
 * school survey preset when the file carries the family pattern
 * — QN question variables, or the weight/stratum/psu name trio.
 * Otherwise generic.
 */
export function detectPreset(table) {
    const names = table.columns.map((c) => c.name.toLowerCase());
    const evidence = [];
    const qnCount = names.filter((n) => /^qn/.test(n)).length;
    if (qnCount >= 2) {
        evidence.push(`${qnCount} columns are named QN..., the school-survey question-variable pattern`);
    }
    // The design trio, under the names these files actually use
    // (the weight arrives as "finalwt", not "weight").
    const trioForms = [
        ['weight', 'finalwt'],
        ['stratum'],
        ['psu'],
    ];
    const trio = trioForms.filter((forms) => forms.some((f) => names.includes(f)));
    if (trio.length === 3) {
        evidence.push('the weight, stratum, and PSU columns match the school-survey design variables');
    }
    const dichot = names.filter((n) => /^qn\d+$/.test(n)).length;
    if (dichot >= 3) {
        evidence.push(`${dichot} dichotomous QN variables (QN followed by digits)`);
    }
    const isSchoolSurvey = evidence.length >= 2 || (qnCount >= 3 && trio.length >= 2);
    return {
        preset: isSchoolSurvey ? SCHOOL_SURVEY_PRESET : GENERIC_PRESET,
        evidence,
    };
}
//# sourceMappingURL=presets.js.map